import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, ToastContext, Form, CounterLabel } from '@weezevent/nacre';
import { useForm, requiredEmailField } from '../../../../utils/validators';
import Style from './subscriptionModal.module.css';
import WaitingListTicketModel from '../../../../models/waitingListTicketModel';

export default function SubscriptionModal({ waitingListTicket = null, organization, event, onClose = () => {}, open = false, waitingList, syncWaitingListTickets = () => {} }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const validateQuantity = React.useCallback(
        (name, quantity, form) => {
            if (quantity < 1 || quantity > waitingList.quantity) {
                return t('ticket.waiting_list.subscribers.quantity_error');
            }
            return true;
        },
        [waitingList]
    );

    const validators = React.useMemo(() => {
        return {
            email: requiredEmailField,
            quantity: validateQuantity
        };
    }, [validateQuantity]);

    const [form, setForm, , formErrors, formValidate, setErrors] = useForm(
        {
            email: waitingListTicket?.email || '',
            quantity: waitingListTicket?.quantity || 1
        },
        validators
    );

    const onSubmit = React.useCallback(() => {
        if (!formValidate()) {
            return;
        }
        setIsSubmitting(true);
        const _waitingListTicket = new WaitingListTicketModel({
            ...form,
            id: waitingListTicket?.id,
            waiting_list: waitingList.id
        });
        _waitingListTicket
            .save({
                organizationId: organization.id,
                eventId: event.id,
                waitingListId: waitingList.id
            })
            .then(() => {
                toast.success(t(`ticket.waiting_list.subscribers.${!waitingListTicket ? 'create' : 'edit'}.toast-success`));
                syncWaitingListTickets();
                onClose();
            })
            .catch(e => {
                if (e.code === 400) {
                    setErrors({ ...formErrors, ...e.response });
                }
                toast.error(t(`ticket.waiting_list.subscribers.${!waitingListTicket ? 'create' : 'edit'}.toast-error`));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [waitingListTicket, waitingList, form, formValidate, organization, event, onClose, toast, t, syncWaitingListTickets]);

    if (!open) {
        return null;
    }

    return (
        <Modal
            title={t(`ticket.waiting_list.subscribers.${!waitingListTicket ? 'create' : 'edit'}.title`)}
            subtitle={t('ticket.waiting_list.subscribers.create.subtitle')}
            allowExternalClosing={false}
            open={Boolean(open)}
            onClose={onClose}
            submitButton={<Button primary label={t('ticket.common.action.confirm')} disabled={Object.keys(formErrors).length > 0} onClick={onSubmit} isLoading={isSubmitting} />}
            cancelButton={<Button onClick={onClose} label={t('ticket.common.action.cancel')} disabled={isSubmitting} />}
        >
            <Form.Field>
                <Form.Label label={t('ticket.waiting_list.subscribers.create.email_label')} />
                <Form.Input
                    value={form.email}
                    placeholder={t('ticket.waiting_list.subscribers.create.email_placeholder')}
                    onChange={({ target: { value } }) => setForm('email', value)}
                />
                <Form.FieldErrors errors={formErrors['email'] ? [t(formErrors['email'])] : ['']} />
            </Form.Field>
            <Form.Field>
                <div className={Style['quantity-counter']}>
                    <CounterLabel
                        label={t('ticket.waiting_list.subscribers.create.quantity_label')}
                        numberDecimals={0}
                        min={1}
                        max={waitingList.quantity}
                        onChange={(e, value) => setForm('quantity', value)}
                        step={1}
                        value={form.quantity}
                    />
                </div>
                <Form.FieldErrors errors={formErrors['quantity'] ? [formErrors['quantity']] : ['']} />
            </Form.Field>
        </Modal>
    );
}
